import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IndianRupee, Wallet, Landmark, TrendingUp, AlertTriangle, LineChart as LineIcon, Store, Globe } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, inr } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";

const Big = ({ icon: Icon, label, value, sub, from, to, onClick, tid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={tid || `biz-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    className="text-left rounded-2xl p-5 text-white shadow-md transition-transform hover:scale-[1.01] disabled:cursor-default" style={{ background: `linear-gradient(135deg,${from},${to})` }}>
    <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-white/80">{label}</span><div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center"><Icon size={18} /></div></div>
    <p className="text-3xl font-extrabold tracking-tight mt-3">{value}</p>
    {sub && <p className="text-white/80 text-xs mt-1">{sub}</p>}
  </button>
);

const Mini = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <p className={`text-2xl font-extrabold tracking-tight mt-1 ${color || "text-slate-900"}`}>{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [t, setT] = useState(null);
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const { subscribe } = useRealtime();
  const load = useCallback(() => { api.get("/admin/tally-dashboard").then((r) => setT(r.data)); api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("order")) load(); }), [subscribe, load]);
  if (!t) return <div className="text-slate-400">Loading dashboard…</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Business Overview</h1>

      {d?.expired_batches > 0 && (
        <button data-testid="dash-expired-alert" onClick={() => nav("/admin/batches")} className="w-full flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm font-semibold hover:bg-red-100 transition-colors">
          <AlertTriangle size={18} /> {d.expired_batches} stock batch(es) expired{d.expiring_batches ? ` · ${d.expiring_batches} expiring within 30 days` : ""} — review & write off →
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Big icon={IndianRupee} label="Total Sales" value={inr(t.total_sales)} sub={`${t.orders_count} orders · Today ${inr(t.today_sales)}`} from="#4338ca" to="#6366f1" />
        <Big icon={Wallet} label="Receivables (Udhaar)" tid="biz-receivables" value={inr(t.receivables)} sub="Tap to view khata →" from="#b45309" to="#f59e0b" onClick={() => nav("/admin/khata")} />
        <Big icon={Landmark} label="Current Balance" value={inr(t.current_balance)} sub="Collected (cash/card/upi)" from="#065f46" to="#10b981" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Mini label="Today Sales" value={inr(t.today_sales)} color="text-sky-600" />
        <Mini label="Total Profit" value={inr(t.total_profit)} color="text-emerald-600" />
        <Mini label="Online Orders" value={d?.online_orders ?? "—"} color="text-indigo-600" />
        <Mini label="POS Orders" value={d?.pos_orders ?? "—"} color="text-purple-600" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-bold mb-4 flex items-center gap-2"><LineIcon size={18} className="text-emerald-600" /> Sales Trend — Last 30 Days</h2>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={t.sales_series}>
            <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0B8E36" stopOpacity={0.35} /><stop offset="100%" stopColor="#0B8E36" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" fontSize={11} interval={4} /><YAxis fontSize={11} />
            <Tooltip formatter={(v) => inr(v)} />
            <Area type="monotone" dataKey="sales" stroke="#0B8E36" strokeWidth={2.5} fill="url(#g1)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100"><h2 className="font-bold">Latest Transactions</h2><button onClick={() => nav("/admin/orders")} className="text-emerald-600 text-sm font-bold">View all</button></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="text-left py-3 px-5">Date</th><th className="text-left py-3 px-4">Type</th><th className="text-left py-3 px-4">Name</th><th className="text-left py-3 px-4">Invoice</th><th className="text-right py-3 px-4">Items</th><th className="text-right py-3 px-4">Profit</th><th className="text-right py-3 px-5">Amount</th></tr></thead>
            <tbody>{t.latest.map((o) => (
              <tr key={o.id} data-testid={`txn-${o.id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => window.open(`/print/invoice/${o.id}`, "_blank")}>
                <td className="py-3 px-5 text-slate-500 whitespace-nowrap">{new Date(o.date).toLocaleDateString("en-IN")}</td>
                <td className="py-3 px-4"><span className={`text-xs font-bold px-2 py-0.5 rounded ${o.type === "pos" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700"}`}>{String(o.type).toUpperCase()}</span></td>
                <td className="py-3 px-4">{o.name}</td>
                <td className="py-3 px-4 font-semibold">{o.invoice_no}</td>
                <td className="py-3 px-4 text-right">{o.items}</td>
                <td className="py-3 px-4 text-right font-semibold text-emerald-600">{inr(o.profit)}</td>
                <td className="py-3 px-5 text-right font-bold">{inr(o.amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
